/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type MouseEvent, useCallback } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import { useRouter, useRouterActions } from 'react-resource-router';

import { token } from '@atlaskit/tokens';

import { clearHash } from '../../../common/deep-link/clearHash';
import { scrollToTop } from '../../../common/deep-link/scrollToTop';
import { CLI_ROUTE_NAME, CLI_ROUTE_PATH, HOME_ROUTE_NAME, HOME_ROUTE_PATH } from '../../../routes';
import { isModifiedClick } from '../../utils/is-modified-click';

import { messages } from './messages';

const styles = cssMap({
	centerNav: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		justifySelf: 'center',
		marginBottom: token('space.negative.050'),
		'@media (max-width: 48rem)': {
			display: 'none',
		},
	},
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: '#B7B9BE',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		// Hover lifts the fill and the label only. The underline is the current-page
		// marker, so drawing it here would tell a visitor they are already on the page
		// they are reaching for.
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: '#FFFFFF',
		},
	},
	buttonActive: {
		color: '#FFFFFF',
		// A border rather than a fill so it survives forced-colors mode, which is
		// why ADS Tabs draws its selected line the same way. `currentColor`
		// rather than `color.border.selected`: that token is #1868DB in the light
		// theme, only 3.6:1 against this bar's hardcoded #111213.
		'&::after': {
			content: '""',
			position: 'absolute',
			insetBlockEnd: 0,
			insetInlineStart: token('space.150'),
			insetInlineEnd: token('space.150'),
			height: 0,
			borderBlockEndWidth: token('border.width.selected'),
			borderBlockEndStyle: 'solid',
			borderBlockEndColor: 'currentColor',
		},
	},
});

type NavItem = {
	/** Route path this item navigates to, and its `href`. */
	path: string;
	/** Route name the current route is matched against for the active state. */
	routeName: string;
	message: MessageDescriptor;
};

/**
 * The whole nav: two page links, identical on every route and in both auth
 * states. In-page section links used to sit alongside them, but mixing anchors
 * with page links made the model hard to read.
 */
const NAV_ITEMS: NavItem[] = [
	{ path: HOME_ROUTE_PATH, routeName: HOME_ROUTE_NAME, message: messages.navAbout },
	{ path: CLI_ROUTE_PATH, routeName: CLI_ROUTE_NAME, message: messages.navCli },
];

const NavItemLink = ({ path, routeName, message }: NavItem): JSX.Element => {
	const [{ route }] = useRouter();
	const { push } = useRouterActions();
	const isActive = route?.name === routeName;

	// Pushing the route the visitor is already on changes nothing, so
	// `useResetScrollOnRouteChange` never fires and the click looks dead to a
	// visitor partway down the page. Scroll here instead, matching `BrandHomeLink`
	// so both of the bar's "back to the top of this page" affordances behave the
	// same way.
	const handleClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();

			if (isActive) {
				scrollToTop();
				clearHash();
				return;
			}

			push(path);
		},
		[isActive, path, push],
	);

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- SPA navigation needs native link semantics
		<a
			href={path}
			css={[styles.button, isActive && styles.buttonActive]}
			aria-current={isActive ? 'page' : undefined}
			onClick={handleClick}
		>
			<FormattedMessage {...message} />
		</a>
	);
};

type NavButtonsProps = {
	ariaLabel: string;
	testId?: string;
};

export const NavButtons = ({ ariaLabel, testId = 'nav-buttons' }: NavButtonsProps): JSX.Element => (
	<nav css={styles.centerNav} aria-label={ariaLabel} data-testid={testId}>
		{NAV_ITEMS.map((item) => (
			<NavItemLink key={item.routeName} {...item} />
		))}
	</nav>
);
